import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CopyOutlined } from "@ant-design/icons";
import { App, Button, theme } from "antd";
import { formatAmount } from "../../../../utils/formatAmount.js";
import { __ } from "../../../../utils/i18n.js";
const CancellationContractSummary = ({ subscription })=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const productName = subscription.data.productPlan.name;
    const productDescription = subscription.data.productPlan.description;
    const { key: contractKey, id: contractId } = subscription.contractOrCustomerOrderNumberLabel;
    const isContract = contractKey === "Contract";
    const contractLabel = isContract ? __("Contract number") : __("Order number");
    // Prefer the price the customer actually pays (contract, else upcoming charge); the plan list price in the
    // subscription's currency is the one-time / lifetime fallback only.
    const { currency } = subscription.data;
    const priceRow = subscription.data.productPlan.price.find((productPlanPrice)=>productPlanPrice.currency === currency);
    const recurringDisplay = subscription.currentRecurringPriceLabel ?? (priceRow ? formatAmount({
        amount: Number(priceRow.recurringPrice),
        currency,
        paymentInterval: subscription.data.productPlan.paymentInterval,
        taxTreatment: subscription.taxTreatment
    }) : "");
    const handleCopyContract = async ()=>{
        try {
            await navigator.clipboard.writeText(contractId);
            message.info(__("%s copied to clipboard.", contractLabel));
        } catch  {
            message.warning({
                content: isContract ? __("Couldn't copy to clipboard. Contract number: %s", contractId) : __("Couldn't copy to clipboard. Order number: %s", contractId),
                duration: 8
            });
        }
    };
    const rowBorder = {
        borderTop: `1px solid ${token.colorSplit}`
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-cancellation-contract-summary",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-cancellation-contract-summary__heading",
                style: {
                    color: token.colorTextHeading
                },
                children: __("Contract summary")
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-cancellation-contract-summary__row",
                style: rowBorder,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-cancellation-contract-summary__label",
                        style: {
                            color: token.colorTextSecondary
                        },
                        children: contractLabel
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "rc-account-v2-cancellation-contract-summary__value rc-account-v2-cancellation-contract-summary__contract",
                        style: {
                            color: token.colorTextHeading
                        },
                        children: [
                            /*#__PURE__*/ _jsx("code", {
                                className: "rc-account-v2-cancellation-contract-summary__contract-id",
                                style: {
                                    color: token.colorText
                                },
                                children: contractId
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                className: "rc-account-v2-cancellation-contract-summary__copy-button",
                                type: "text",
                                size: "small",
                                icon: /*#__PURE__*/ _jsx(CopyOutlined, {}),
                                onClick: handleCopyContract,
                                "aria-label": isContract ? __("Copy contract number") : __("Copy order number")
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-cancellation-contract-summary__row",
                style: rowBorder,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-cancellation-contract-summary__label",
                        style: {
                            color: token.colorTextSecondary
                        },
                        children: __("Product & plan")
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "rc-account-v2-cancellation-contract-summary__value",
                        style: {
                            color: token.colorTextHeading
                        },
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "rc-account-v2-cancellation-contract-summary__product-name",
                                children: productName
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "rc-account-v2-cancellation-contract-summary__product-description",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: productDescription
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-cancellation-contract-summary__row",
                style: rowBorder,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-cancellation-contract-summary__label",
                        style: {
                            color: token.colorTextSecondary
                        },
                        children: __("Payment terms")
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-cancellation-contract-summary__value rc-account-v2-cancellation-contract-summary__value--bold",
                        style: {
                            color: token.colorTextHeading
                        },
                        children: subscription.hasNextRenewalDiscount ? /*#__PURE__*/ _jsxs("span", {
                            className: "rc-account-v2-cancellation-contract-summary__discount",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                color: token.colorSuccess
                                            },
                                            children: subscription.nextBillingChargeLabel
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "rc-account-v2-cancellation-contract-summary__discount-strike",
                                            style: {
                                                color: token.colorTextTertiary
                                            },
                                            children: subscription.contractPriceLabel
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "rc-account-v2-cancellation-contract-summary__discount-note",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            style: {
                                                color: token.colorSuccess
                                            },
                                            children: __("Your next renewal discount is locked in.")
                                        }),
                                        " ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "rc-cancel-loss-lead",
                                            children: __("Cancelling now gives it up.")
                                        })
                                    ]
                                })
                            ]
                        }) : recurringDisplay
                    })
                ]
            })
        ]
    });
};
export { CancellationContractSummary };
